import { Navigate, Route, Routes } from 'react-router-dom';
import { SignupPage } from 'src/v2/pages/auth/SignupPage';
import { SigninPage } from 'src/v2/pages/auth/SigninPage';
import {
  IS_SIGN_UP_REDIRECT,
  isEnterprise,
  isEmailSignupEnabled,
} from 'src/utils';
import { ManageTasksChatPage } from 'src/pages/ManageTasksChatPage';
import { APINestedRoutes, AppRoutes, RouteItem } from 'src/types';
import { useMemo } from 'react';
import { VerificationPage } from 'src/v2/pages/auth/VerificationPage';
import { VerificationFailedPage } from 'src/v2/pages/auth/VerificationFailedPage';
import { ForgotPasswordPage } from 'src/v2/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from 'src/v2/pages/auth/ResetPasswordPage';
import { ResetPasswordFailedPage } from 'src/v2/pages/auth/ResetPasswordFailedPage';
import { SignupWithEmailPage } from 'src/v2/pages/auth/SignupWithEmailPage';
import { SigninWithEmailPage } from 'src/v2/pages/auth/SigninWithEmailPage';
import { WelcomeEnterprisePage } from 'src/v2/pages/auth/WelcomeEnterprisePage';
import { InviteToTeamPage } from 'src/v2/pages/invitation';
import { AmpOnboardingPage } from 'src/v2/pages/auth/AmpOnboardingPage';
import { NotFound } from 'src/pages/NotFound';
import { PageLayoutWithNavMenu } from 'src/components/PageLayoutWithNavMenu';
import { ManageAccountPage } from 'src/pages/ManageAccountPage';
import { APIKeys } from 'src/pages/ManageAccountPage/components/APIKeys';
import { ReferralPage } from 'src/v2/pages/referral';

const ENTERPRISE_MANAGE_ACCOUNT_ROUTES: string[] = [
  APINestedRoutes.API_KEYS,
  APINestedRoutes.API_USAGE,
];

const defaultRoutes: RouteItem[] = [
  {
    element: <PageLayoutWithNavMenu />,
    children: [
      {
        path: AppRoutes.HOME,
        element: <ManageTasksChatPage />,
      },
      {
        path: AppRoutes.NOT_FOUND,
        element: <NotFound />,
      },
      {
        path: AppRoutes.MANAGE_ACCOUNT,
        element: <ManageAccountPage />,
        children: [
          {
            index: true,
            path: APINestedRoutes.API_KEYS,
            element: <APIKeys />,
          },
        ],
      },
      // {
      //   path: AppRoutes.VIDEO_TUTORIALS,
      //   element: <VideoTutorialsPage />,
      // },
    ],
  },
  {
    path: AppRoutes.LOGIN,
    element: <SigninPage />,
  },
  {
    path: AppRoutes.LOGIN_WITH_EMAIL,
    element: <SigninWithEmailPage />,
  },
  {
    path: AppRoutes.SIGN_UP,
    element: <SignupPage />,
  },
  ...(isEmailSignupEnabled
    ? [
        {
          path: AppRoutes.SIGN_UP_WITH_EMAIL,
          element: <SignupWithEmailPage />,
        },
      ]
    : []),
  {
    path: AppRoutes.FORGOT_PASSWORD,
    element: <ForgotPasswordPage />,
  },
  {
    path: AppRoutes.RESET_PASSWORD,
    element: <ResetPasswordPage />,
  },
  {
    path: AppRoutes.VERIFICATION_FAILED,
    element: <VerificationFailedPage />,
  },
  {
    path: AppRoutes.VERIFIED,
    element: <VerificationPage />,
  },
  {
    path: AppRoutes.PASSWORD_RESET_FAILED,
    element: <ResetPasswordFailedPage />,
  },
  {
    path: AppRoutes.INVITE,
    element: <InviteToTeamPage />,
  },
  {
    path: AppRoutes.AMP_ONBOARD,
    element: <AmpOnboardingPage />,
  },
  {
    path: AppRoutes.REFERRAL,
    element: <ReferralPage />,
  },
];

// no HOME for Enterprise
const ENTERPRISE_ROUTES = [
  AppRoutes.LOGIN,
  AppRoutes.SIGN_UP,
  AppRoutes.FORGOT_PASSWORD,
  AppRoutes.RESET_PASSWORD,
  AppRoutes.VERIFICATION_FAILED,
  AppRoutes.VERIFIED,
  AppRoutes.PASSWORD_RESET_FAILED,
];

export const PublicContent = () => {
  const isOnboardingRedirect = localStorage.getItem(IS_SIGN_UP_REDIRECT);

  const routes = useMemo(() => {
    if (isEnterprise) {
      return defaultRoutes.filter((route) =>
        ENTERPRISE_ROUTES.includes(route.path as AppRoutes),
      );
    } else {
      return defaultRoutes;
    }
  }, []);

  return (
    <Routes>
      {routes.map((route, index) => (
        <Route key={index} element={route.element}>
          {route.children ? (
            route.children.map((childRoute, childIndex) => (
              <Route
                key={childIndex}
                path={childRoute.path}
                element={childRoute.element}
              >
                {childRoute.children &&
                  (isEnterprise
                    ? childRoute.children.filter(
                        (item) =>
                          !item.path ||
                          ENTERPRISE_MANAGE_ACCOUNT_ROUTES.includes(item.path),
                      )
                    : childRoute.children
                  ).map((grandChild, grandChildIndex) => (
                    <Route
                      key={grandChildIndex}
                      index={grandChild.index}
                      path={grandChild.path}
                      element={grandChild.element}
                    />
                  ))}
              </Route>
            ))
          ) : (
            <Route path={route.path} element={route.element} />
          )}
        </Route>
      ))}

      {isEnterprise && (
        <Route path={AppRoutes.WELCOME} element={<WelcomeEnterprisePage />} />
      )}
      {/* for Enterprise, we redirect to Welcome page instead of Home page */}
      <Route
        path={AppRoutes.WILDCARD}
        element={
          <Navigate
            to={
              isOnboardingRedirect
                ? AppRoutes.SIGN_UP
                : isEnterprise
                  ? AppRoutes.WELCOME
                  : AppRoutes.HOME
            }
            replace
          />
        }
      />
    </Routes>
  );
};
